import { Injectable } from '@angular/core';
import { CreateEraInput, CreateEraOutput, CreateMutOutput, EraServiceProxy, ImportEmployeesExcelFileDto, ImportEmployeesExcelRowDto, LegitimationStatus, MySironaGetCorporateLicenceDetailedDto, MySironaGetCorporateLicenceDto, MySironaGetSponsorshipContactPersonDto, MySironaGetSponsorshipDataDto, MySironaGetSponsorshipEmployeeDto, MySironaLegitimatedUser, MySironaLegitimationServiceProxy, MySironaSponsorshipServiceProxy, SubmitEmployeesExcelImportInput, SubmitEmployeesExcelImportRowResult } from '@shared/service-proxies/service-proxies';
import { DateTime } from 'luxon';
import { Observable, of, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MySironaSponsorshipService {
  sponsorshipId: number;
  sponsorshipDataCached: MySironaGetSponsorshipDataDto;
  codes: any;

  constructor(
    private readonly mySironaSponsorshipServiceProxy: MySironaSponsorshipServiceProxy,
    private readonly eraServiceProxy: EraServiceProxy,
  ) {}

  public setSponsorshipId(id: number): void {
    this.sponsorshipId = id;
    this.sponsorshipDataCached = undefined; // Clear cached data when setting a new sponsorship ID
  }

  public getSponsorshipData(): Observable<MySironaGetSponsorshipDataDto> {
    if (this.sponsorshipDataCached !== undefined) {
      return of(this.sponsorshipDataCached);
    }

    return this.mySironaSponsorshipServiceProxy
      .getSponsorshipData(this.sponsorshipId)
      .pipe(tap((x) => (this.sponsorshipDataCached = x)));
  }

  public getSponsorshipContactPerson(): Observable<MySironaGetSponsorshipContactPersonDto> {
    return this.mySironaSponsorshipServiceProxy.getSponsorshipContactPerson(this.sponsorshipId);
  }

  public getLicencesForSponsorship(): Observable<MySironaGetCorporateLicenceDto[]> {
    return this.mySironaSponsorshipServiceProxy.getLicencesForSponsorship(this.sponsorshipId);
  }

  public getCorporateLicence(licenceId: number): Observable<MySironaGetCorporateLicenceDetailedDto> {
    return this.mySironaSponsorshipServiceProxy.getCorporateLicence(licenceId);
  }

  public hasAnyPublishedLicence(): Observable<boolean> {
    return this.mySironaSponsorshipServiceProxy.hasAnyPublishedLicence(this.sponsorshipId);
  }

  public hasSponsorshipMutApplications(): Observable<number> {
    return this.mySironaSponsorshipServiceProxy.hasSponsorshipMutApplications(this.sponsorshipId);
  }

  public createMut(mutationType: string, corporateLicenceId: number): Observable<CreateMutOutput> {
    return this.mySironaSponsorshipServiceProxy.createMut(mutationType, this.sponsorshipId, corporateLicenceId);
  }

  public getLegitimatedAdmins(): Observable<MySironaLegitimatedUser[]> {
    return this.mySironaSponsorshipServiceProxy.getLegitimations(this.sponsorshipId, true);
  }

  public getLegitimatedUsers(): Observable<MySironaLegitimatedUser[]> {
    return this.mySironaSponsorshipServiceProxy.getLegitimations(this.sponsorshipId, false);
  }

  public getMyLegitimation(): Observable<MySironaLegitimatedUser> {
    return this.mySironaSponsorshipServiceProxy.getMyLegitimation(this.sponsorshipId);
  }

  public setLegitimationToInactiveForUser(): Observable<void> {
    return this.mySironaSponsorshipServiceProxy.setLegitimationToInactiveForUser(this.sponsorshipId);
  }

  public removeAdminRightsForUser(): Observable<void> {
    return this.mySironaSponsorshipServiceProxy.removeAdminRightsForUser(this.sponsorshipId);
  }

  public updateLegitimationStatus(legitimationId: number, status: LegitimationStatus): Observable<void> {
    return this.mySironaSponsorshipServiceProxy.updateLegitimationStatus(this.sponsorshipId, legitimationId, status);
  }

  public approveOrRefuse(legitimationId: number, approve: boolean): Observable<void> {
    return this.mySironaSponsorshipServiceProxy.approveOrRefuseLegitimation(this.sponsorshipId, legitimationId, approve);
  }

  public inviteNewUser(email: string): Observable<void> {
    return this.mySironaSponsorshipServiceProxy.inviteNewUser(this.sponsorshipId, email);
  }

  public getVoucherAllowances(): Observable<MySironaGetCorporateLicenceDto[]> {
    return this.mySironaSponsorshipServiceProxy.getVoucherAllowances(this.sponsorshipId);
  }

  public getEmployeesForSponsorship(): Observable<MySironaGetSponsorshipEmployeeDto[]> {
    return this.mySironaSponsorshipServiceProxy.getEmployeesForSponsorship(this.sponsorshipId);
  }

  public endEmployment(personId: number, endDate: DateTime): Observable<void> {
    return this.mySironaSponsorshipServiceProxy.endEmployment(this.sponsorshipId, personId, endDate);
  }

  public parseEmployeesExcelImport(file: ImportEmployeesExcelFileDto): Observable<ImportEmployeesExcelRowDto[]> {
    return this.mySironaSponsorshipServiceProxy.parseEmployeesExcelImport(this.sponsorshipId, file);
  }

  public submitEmployeesExcelImport(input: SubmitEmployeesExcelImportInput): Observable<SubmitEmployeesExcelImportRowResult[]> {
    return this.mySironaSponsorshipServiceProxy.submitEmployeesExcelImport(this.sponsorshipId, input);
  }

  public createEraForCurrentSponsorship(): Observable<CreateEraOutput> {
    return this.getMyLegitimation().pipe(
      switchMap((legitimation) => this.eraServiceProxy.createEra(new CreateEraInput({ legitimationId: legitimation.legitimationId })))
    );
  }
}
